import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@web/components/AuthLayout";
import { useSession } from "@web/auth/session";
import { beginLogin, isSsoConfigured } from "@web/auth/oidc";
import { Role } from "@web/auth/capabilities";

// Sign-in for the operator console (ADR-0014 §6 phase 2). The same authorization-code + PKCE flow the
// product uses — `@web/auth/oidc`, one implementation — but against THIS entry's Keycloak client
// (`clms-platform`, defined in vite.config.ts and read through `config.oidcClientId`). That client is
// the whole reason this host exists: C-2's platform guard asks whether the call arrived on the platform
// client AND the caller holds platform authority, and only a token minted for this client can answer yes.
//
// It is NOT apps/web's LoginPage, and the differences are the point rather than styling:
//   • no tenant. The console addresses every tenant and none, so `beginLogin` is called with
//     `tenant: null` — no `tenant` parameter rides to Keycloak, no `X-Tenant` goes out (the host's
//     `platform` label is reserved in `resolveTenantSlug`), and the branding slot shows the platform's
//     own name instead of an organisation's.
//   • no external-learner email link. That flow issues a learner session for ONE tenant; it has no
//     meaning on a cross-tenant console and would be an entry point nobody here can use.
//   • no "organisation not found" state. There is no organisation in this URL to fail to find.
//   • no `kc_idp_hint`. A per-tenant broker federates ONE customer's directory (ADR-0014 §12 C-6) and
//     stamps its `tenant_id` on whoever comes through it; the operators who sign in here are Verso's own
//     and belong to no customer tenant, so there is no customer provider to hint at. `idpHint: null` is
//     therefore the console's permanent answer and not a gap — and since the hint is resolved from the
//     tenant in the URL, a console that tried would have nothing to resolve it from.
export function PlatformLoginPage() {
  const { t, i18n } = useTranslation();
  const { setSession } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const expired = params.get("reason") === "session_expired";
  const returnTo = params.get("returnTo") ?? "/";

  function signIn() {
    const locale = i18n.language.split("-")[0];
    if (isSsoConfigured()) {
      void beginLogin({ tenant: null, returnTo, locale, idpHint: null });
      return;
    }
    // Dev fallback (no IdP configured locally): establish a placeholder session so the console's
    // routes stay exercisable without a running Keycloak, exactly as the product's LoginPage does.
    // PLATFORM_ADMIN alone — this app gates every route on it and holds nothing else, so handing out
    // the tenant-admin roles here would misrepresent what the console is.
    setSession({
      userId: "demo-platform",
      displayName: "Demo (alustan ylläpitäjä)",
      tenantSlug: null,
      roles: [Role.PlatformAdmin],
    });
    void navigate(returnTo, { replace: true });
  }

  return (
    <AuthLayout platformName={t("platform.brand")}>
      <div className="auth__head">
        <h1 className="auth__title">{t("auth.loginTitle")}</h1>
        <p className="auth__text">{t("platform.signInIntro")}</p>
      </div>

      {expired && (
        <p role="alert" className="banner banner--warn" data-testid="session-expired">
          {t("auth.sessionExpired")}
        </p>
      )}

      <div className="auth__actions">
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={signIn}
          data-testid="platform-sign-in"
        >
          {t("auth.loginWithOrg")}
        </button>
      </div>
    </AuthLayout>
  );
}
